import { FormField } from "@/components/forms/FormField.tsx";
import { FormMaxWidthWrapper } from "@/components/forms/FormMaxWidthWrapper.tsx";
import { RadioGroupDiv } from "@/components/forms/RadioGroupDiv.tsx";
import { WModalContent } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { VmStopModalHeaderBlock, type VmCommonFields } from "@/modules/compute/ModalHeaderBlock.tsx";
import { VmSizeSelect } from "@/modules/compute/VmSizeSelect.tsx";
import { getSizeParams } from "@/modules/compute/commonViewModel.ts";
import { extractOsFields } from "@/modules/compute/os/os.ts";
import { addDiskMutation, resizeDiskMutation } from "@/modules/compute/vmQuery.ts";
import { getDiskPackages, getDiskRanges, SIZE_DISABLED, type SizeValue } from "@/modules/compute/vmSizeSelectUtils.ts";
import { canCreateResourceHostPoolsAtom } from "@/modules/pools/hostPoolsQuery.ts";
import { getDefaultHostPool } from "@/modules/pools/logic.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { VirtualMachineStorage, VmAddDiskBody, VmResizeDiskBody } from "@warrenio/api-spec/spec.oats.gen";
import { useMemo, useState } from "react";
import { last } from "remeda";
import invariant from "tiny-invariant";

interface DiskResizeModalProps {
    vm: VmCommonFields;
    /** Selected disk to resize, or null if adding a new disk */
    disk: VirtualMachineStorage | null;

    onAddDisk?: (params: { location: string; body: VmAddDiskBody }) => Promise<void>;
    onResizeDisk: (params: { location: string; body: VmResizeDiskBody }) => Promise<void>;
}

export function DiskAddOrResizeModal(props: Omit<DiskResizeModalProps, "onAddDisk" | "onResizeDisk">) {
    //#region Hooks
    const createMutation = useStandardMutation(addDiskMutation);
    const resizeMutation = useStandardMutation(resizeDiskMutation);
    //#endregion

    return (
        <BaseDiskModal
            {...props}
            onAddDisk={async (params) => {
                await createMutation.mutateAsync(params);
            }}
            onResizeDisk={async (params) => {
                await resizeMutation.mutateAsync(params);
            }}
        />
    );
}

export function BaseDiskModal({ vm, disk, onAddDisk, onResizeDisk }: DiskResizeModalProps) {
    const { t } = useLingui();
    const pools = useSuspenseQueryAtom(canCreateResourceHostPoolsAtom);
    const { status, location, designated_pool_uuid, uuid } = vm;

    invariant(disk != null || onAddDisk, "onAddDisk is required when existing disk is not provided");

    //#region Hooks

    const pool = getDefaultHostPool(pools, designated_pool_uuid);
    const sizeParams = getSizeParams(pool?.guest_limits);
    const currentDiskSize = disk ? disk.size : 1;
    sizeParams.ssd.min = currentDiskSize;

    const diskRanges = useMemo(
        // NB: Operating system is `undefined` because we do not want to enforce any constraints based on the OS when adding non-boot disks (and they are irrelevant when resizing)
        () => getDiskRanges(sizeParams, undefined, { currentDiskSize }),
        [sizeParams, currentDiskSize],
    );
    const firstItem = disk ? diskRanges[0] : diskRanges[1];
    const minDiskSize = firstItem;
    const [size, setSize] = useState<SizeValue>({
        disks: firstItem,
        vcpu: SIZE_DISABLED,
        ram: SIZE_DISABLED,
        isCustom: true,
    });
    //#endregion Hooks

    const packages = getDiskPackages(extractOsFields(vm), last(diskRanges)!);

    function handleChangeSize(newValue: SizeValue) {
        setSize(newValue);
    }

    async function onSubmit() {
        if (disk) {
            await onResizeDisk({
                location,
                body: { uuid, disk_uuid: disk.uuid, size_gb: size.disks, check_vm_state: false },
            });
        } else {
            invariant(onAddDisk);
            await onAddDisk({ location, body: { uuid, size_gb: size.disks } });
        }
    }

    return (
        <WModalContent
            title={disk ? t`Resize disk` : t`Add additional disk`}
            label={disk ? t`Resize` : t`Add`}
            footerNotice={
                disk && status === "running" && disk.primary
                    ? t`Warning: It is recommended to stop the virtual machine before resizing the boot disk.`
                    : undefined
            }
            modalAction={onSubmit}
        >
            <VmStopModalHeaderBlock vm={vm} showStopButton={!!disk} />

            <FormMaxWidthWrapper>
                <FormField wide label={t`Size`} block isRequired>
                    <RadioGroupDiv>
                        <VmSizeSelect
                            vmData={{ location, designated_pool_uuid, ...extractOsFields(vm) }}
                            sliderRanges={{
                                ssd: diskRanges,
                                cpu: [],
                                ram: [],
                            }}
                            value={size}
                            onChange={handleChangeSize}
                            packages={packages}
                            diskOnly
                            isDiskPrimary={disk ? disk.primary : false}
                            sizeParams={sizeParams}
                        />
                    </RadioGroupDiv>
                </FormField>
            </FormMaxWidthWrapper>

            {disk && (
                <p>
                    <Trans>Disk size can only be increased. This action is not reversible. Minimum disk size:</Trans>{" "}
                    <b>
                        {minDiskSize} <Trans>GB</Trans>
                    </b>
                    .
                </p>
            )}
        </WModalContent>
    );
}
